<template>
  <div class="bg-white rounded-xl shadow p-6">
    <!-- Header & Filters -->
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-semibold text-gray-800">Banner Statistics</h2>

      <div class="flex space-x-2">
        <input 
          type="date"
          v-model="startDate"
          class="border rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="date"
          v-model="endDate"
          class="border rounded-lg px-3 py-2 text-sm"
        />
        <button
          @click="fetchAnalytics"
          class="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg shadow transition"
        >
          Filter
        </button>
        <button
          @click="exportToCSV"
          class="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded-lg shadow transition"
        >
          Export CSV
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="min-w-full border border-gray-200">
        <thead class="bg-gray-50">
          <tr>           
            <th class="px-4 py-2 text-left text-sm font-semibold text-gray-600 border-b">Banner</th>
            <th class="px-4 py-2 text-right text-sm font-semibold text-gray-600 border-b">Impressions</th>
            <th class="px-4 py-2 text-right text-sm font-semibold text-gray-600 border-b">Clicks</th>
            <th class="px-4 py-2 text-right text-sm font-semibold text-gray-600 border-b">CTR (%)</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in analytics"
            :key="index"
            class="hover:bg-gray-50 transition"
          >           
            <td class="px-4 py-2 border-b">{{ row.bannername }}</td>
            <td class="px-4 py-2 border-b text-right">{{ formatNumber(row.impressions) }}</td>
            <td class="px-4 py-2 border-b text-right">{{ formatNumber(row.clicks) }}</td>
            <td class="px-4 py-2 border-b text-right">
              {{ calcCTR(row.clicks, row.impressions) }}
            </td>
          </tr>

          <tr v-if="!analytics.length">
            <td colspan="6" class="px-4 py-4 text-center text-gray-500">
              No analytics data found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="totalPages > 1"
      class="flex justify-between items-center mt-4"
    >
      <button
        @click="prevPage"
        :disabled="page === 1"
        class="px-4 py-2 bg-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-50"
      >
        Previous
      </button>

      <span class="text-sm text-gray-600">
        Page {{ page }} of {{ totalPages }}
      </span>

      <button
        @click="nextPage"
        :disabled="page === totalPages"
        class="px-4 py-2 bg-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-50"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AdAnalytics",
  data() {
    const today = new Date().toISOString().split("T")[0];
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

    return {
      analytics: [],
      startDate: weekAgo,
      endDate: today,
      page: 1,
      perPage: 50,
      totalRecords: 0,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalRecords / this.perPage);
    },
  },
  mounted() {
   // this.fetchAnalytics();
  },
  methods: {
    async fetchAnalytics() {
      try {
        const LOGIN_API_URL = "https://ads.brandfi.co.ke/ads/adsuiapi/login.php";
        const loginParams = {
          username: "b.arudo@saritcentre.com",
          password: "b.arudo@saritcentre.com",
          client_id: "BrandFiApiUI",
          client_secret: "BrandFiApiUI",
          grant_type: "password",
        };

        // Step 1: Authenticate
        const loginRes = await axios.post(LOGIN_API_URL, loginParams, {
          headers: { "Content-Type": "application/json" },
        });

        const { access_token } = loginRes.data;

        // Step 2: Fetch analytics with pagination
        const API_URL =
          "https://ads.brandfi.co.ke/ads/adsuiapi/agency/banner-stats.php?access_token=" +
          access_token;

        const response = await axios.post(API_URL, {
          account_type: "MANAGER",
          start: this.startDate,
          end: this.endDate,
          agency_id: "15",
          bannerid: 0,
          campaignid: 0,
          clientid: 0,
          page: this.page,
          limit: this.perPage,
        });

        this.analytics = response.data.data || [];
        this.totalRecords = response.data.count || this.analytics.length;
      } catch (err) {
        console.error("❌ Failed to load analytics:", err);
      }
    },

    nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
        this.fetchAnalytics();
      }
    },

    prevPage() {
      if (this.page > 1) {
        this.page--;
        this.fetchAnalytics();
      }
    },

    formatNumber(value) {
      if (!value) return "0";
      return Number(value).toLocaleString();
    },

    calcCTR(clicks, impressions) {
      if (!impressions || impressions === 0) return "0.00";
      return ((clicks / impressions) * 100).toFixed(2);
    },

    exportToCSV() {
      if (!this.analytics.length) return alert("No data to export.");

      const headers = ["Banner,Impressions,Clicks,CTR (%)"];
      const rows = this.analytics.map((row) => [
        row.bannername,
        row.impressions,
        row.clicks,
        this.calcCTR(row.clicks, row.impressions),
      ]);

      const csvContent =
        headers.concat(rows.map((r) => r.join(","))).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", "ad_analytics.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
table th,
table td {
  white-space: nowrap;
}
</style>
